import { createGlContext, uploadMask, type GlContext, type GlTarget } from '../../core/gl';
import { clamp01 } from '../../core/math';
import { resolvePalette } from '../../core/palette';
import { createCanvas, get2d, rasteriseContours } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import { GRAY_SCOTT_PRESETS, type ReactionDiffusionValues } from './params';

/**
 * Gray-Scott reaction-diffusion on the GPU.
 *
 * Two chemicals: A is fed in everywhere, B consumes it and is killed off. The whole
 * variety of patterns comes from the balance between those two rates, which is why the
 * presets matter more than the sliders — most of the parameter space is blank or mush.
 *
 * The simulation runs in a half-float ping-pong pair at its own resolution, independent
 * of the canvas: the pattern scale is set by the *grid*, so a fixed simulation size
 * keeps the look consistent when the document size changes, and the display pass scales
 * the result up.
 */
const SIMULATE_FRAGMENT = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;

uniform sampler2D uState;
uniform sampler2D uMask;
uniform vec2 uTexel;
uniform float uFeed;
uniform float uKill;
uniform float uDa;
uniform float uDb;
uniform float uAniso;
uniform float uBoundary;

void main() {
  vec2 st = vUv;
  vec2 e = uTexel;
  // Anisotropy stretches the sampling kernel, which biases the pattern's direction.
  vec2 ea = vec2(e.x * (1.0 + uAniso), e.y * (1.0 - uAniso));

  vec2 c = texture(uState, st).rg;

  // Standard five-point Laplacian with diagonal weights (Gray-Scott convention).
  vec2 lap =
      texture(uState, st + vec2(-ea.x, 0.0)).rg * 0.2
    + texture(uState, st + vec2( ea.x, 0.0)).rg * 0.2
    + texture(uState, st + vec2(0.0, -ea.y)).rg * 0.2
    + texture(uState, st + vec2(0.0,  ea.y)).rg * 0.2
    + texture(uState, st + vec2(-ea.x, -ea.y)).rg * 0.05
    + texture(uState, st + vec2( ea.x, -ea.y)).rg * 0.05
    + texture(uState, st + vec2(-ea.x,  ea.y)).rg * 0.05
    + texture(uState, st + vec2( ea.x,  ea.y)).rg * 0.05
    - c;

  float a = c.r;
  float b = c.g;
  float reaction = a * b * b;

  float na = a + (uDa * lap.r - reaction + uFeed * (1.0 - a));
  float nb = b + (uDb * lap.g + reaction - (uKill + uFeed) * b);

  if (uBoundary > 0.5) {
    // Hold the chemistry fixed outside the letterforms so the pattern is confined.
    float mask = texture(uMask, st).r;
    if (mask < 0.5) { na = 1.0; nb = 0.0; }
  }

  outColor = vec4(clamp(na, 0.0, 1.0), clamp(nb, 0.0, 1.0), 0.0, 1.0);
}`;

/**
 * Copies an 8-bit seed texture into the float simulation target.
 *
 * Uploading the seed straight into the ping texture would replace its internal format
 * and leave the framebuffer incomplete — the state has to be *rendered* in, not
 * uploaded.
 */
const SEED_FRAGMENT = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uSeed;
void main() { outColor = vec4(texture(uSeed, vUv).rg, 0.0, 1.0); }`;

const DISPLAY_FRAGMENT = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;

uniform sampler2D uState;
uniform sampler2D uRamp;
uniform float uContrast;
uniform float uOpacity;

void main() {
  float b = texture(uState, vUv).g;
  float t = clamp(pow(clamp(b * 3.2, 0.0, 1.0), 1.0 / uContrast), 0.0, 1.0);
  vec3 color = texture(uRamp, vec2(t, 0.5)).rgb;
  // Alpha follows the concentration so the layer composites over what is beneath it.
  outColor = vec4(color, uOpacity * smoothstep(0.02, 0.16, b));
}`;

export interface ReactionDiffusionState {
  gl: GlContext | null;
  /** Fallback message when WebGL2 is unavailable. */
  error: string | null;
  ping: GlTarget | null;
  pong: GlTarget | null;
  mask: GlTarget | null;
  ramp: GlTarget | null;
  simulateProgram: WebGLProgram | null;
  displayProgram: WebGLProgram | null;
  simWidth: number;
  simHeight: number;
  stepsDone: number;
  targetSteps: number;
  /** Result blitted to a 2D canvas so `draw` can composite like every other technique. */
  output: OffscreenCanvas | HTMLCanvasElement;
  octx: Ctx2D;
  dirty: boolean;
}

function values(ctx: TechniqueContext): ReactionDiffusionValues {
  return ctx.values as unknown as ReactionDiffusionValues;
}

function rates(v: ReactionDiffusionValues): { feed: number; kill: number } {
  const preset = GRAY_SCOTT_PRESETS.find((p) => p.value === v.preset);
  if (!preset || preset.value === 'custom') return { feed: v.feed, kill: v.kill };
  return { feed: preset.feed, kill: preset.kill };
}

export function init(ctx: TechniqueContext): ReactionDiffusionState {
  const v = values(ctx);
  const output = createCanvas(ctx.width, ctx.height);

  const state: ReactionDiffusionState = {
    gl: null,
    error: null,
    ping: null,
    pong: null,
    mask: null,
    ramp: null,
    simulateProgram: null,
    displayProgram: null,
    simWidth: 0,
    simHeight: 0,
    stepsDone: 0,
    targetSteps: v.totalSteps,
    output,
    octx: get2d(output),
    dirty: true,
  };

  const gl = createGlContext(ctx.width, ctx.height);
  if (!gl) {
    state.error = 'WebGL2 ist hier nicht verfügbar.';
    state.targetSteps = 0;
    return state;
  }
  state.gl = gl;

  const aspect = ctx.height / Math.max(1, ctx.width);
  const simWidth = Math.max(64, Math.min(2048, Math.round(v.resolution)));
  const simHeight = Math.max(64, Math.round(simWidth * aspect));
  state.simWidth = simWidth;
  state.simHeight = simHeight;

  try {
    state.simulateProgram = gl.createProgram(SIMULATE_FRAGMENT);
    state.displayProgram = gl.createProgram(DISPLAY_FRAGMENT);
  } catch (error) {
    state.error = (error as Error).message;
    state.targetSteps = 0;
    return state;
  }

  state.ping = gl.createTarget(simWidth, simHeight, true);
  state.pong = gl.createTarget(simWidth, simHeight, true);

  // ── Seed the chemistry ───────────────────────────────────────────────────
  // Contours are in canvas space, so they are rescaled to the simulation grid before
  // rasterising — the simulation runs at its own resolution on purpose.
  const scaleX = simWidth / ctx.width;
  const scaleY = simHeight / ctx.height;
  const scaled = ctx.field.contours.map((contour) => {
    const points = new Float32Array(contour.points.length);
    for (let i = 0; i < points.length; i += 2) {
      points[i] = contour.points[i] * scaleX;
      points[i + 1] = contour.points[i + 1] * scaleY;
    }
    return { ...contour, points };
  });
  const maskRaster = rasteriseContours(scaled, simWidth, simHeight, 1);

  state.mask = uploadMask(gl, maskRaster.alpha, simWidth, simHeight);

  const seed = new Uint8Array(simWidth * simHeight * 4);
  for (let i = 0; i < simWidth * simHeight; i++) {
    const inside = maskRaster.alpha[i] > 127;
    let b = 0;
    if (v.seedMode === 'text' || v.seedMode === 'textAndNoise') b = inside ? 1 : 0;
    else if (v.seedMode === 'outline') {
      // A thin band around the contour: inside but close to an outside neighbour.
      const x = i % simWidth;
      const y = (i / simWidth) | 0;
      const neighbour =
        x > 0 && x < simWidth - 1 && y > 0 && y < simHeight - 1
          ? maskRaster.alpha[i - 1] > 127 && maskRaster.alpha[i + 1] > 127 && maskRaster.alpha[i - simWidth] > 127 && maskRaster.alpha[i + simWidth] > 127
          : true;
      b = inside && !neighbour ? 1 : 0;
    }
    if ((v.seedMode === 'random' || v.seedMode === 'textAndNoise') && ctx.rng.chance(0.0016)) b = 1;

    seed[i * 4] = 255;
    seed[i * 4 + 1] = b ? 255 : 0;
    seed[i * 4 + 2] = 0;
    seed[i * 4 + 3] = 255;
  }

  const raw = gl.gl;
  const seedTexture = gl.createTarget(simWidth, simHeight, false);
  raw.bindTexture(raw.TEXTURE_2D, seedTexture.texture);
  raw.texImage2D(raw.TEXTURE_2D, 0, raw.RGBA8, simWidth, simHeight, 0, raw.RGBA, raw.UNSIGNED_BYTE, seed);
  raw.bindTexture(raw.TEXTURE_2D, null);

  try {
    const seedProgram = gl.createProgram(SEED_FRAGMENT);
    raw.disable(raw.BLEND);
    gl.bind(state.ping);
    gl.draw(seedProgram, { uSeed: seedTexture });
  } catch (error) {
    state.error = (error as Error).message;
    state.targetSteps = 0;
    return state;
  }

  // ── Colour ramp as a 1D texture ──────────────────────────────────────────
  const palette = resolvePalette(v.palette, ctx.rng.fork(1));
  const rampWidth = 256;
  const rampBytes = new Uint8Array(rampWidth * 4);
  for (let i = 0; i < rampWidth; i++) {
    const offset = Math.round((i / (rampWidth - 1)) * 255) * 3;
    rampBytes[i * 4] = palette.ramp[offset];
    rampBytes[i * 4 + 1] = palette.ramp[offset + 1];
    rampBytes[i * 4 + 2] = palette.ramp[offset + 2];
    rampBytes[i * 4 + 3] = 255;
  }
  state.ramp = gl.createTarget(rampWidth, 1, false);
  raw.bindTexture(raw.TEXTURE_2D, state.ramp.texture);
  raw.texImage2D(raw.TEXTURE_2D, 0, raw.RGBA8, rampWidth, 1, 0, raw.RGBA, raw.UNSIGNED_BYTE, rampBytes);

  return state;
}

export function step(state: ReactionDiffusionState, _dt: number, ctx: TechniqueContext): ReactionDiffusionState {
  if (state.stepsDone >= state.targetSteps || !state.gl || !state.ping || !state.pong || !state.simulateProgram) {
    return state;
  }
  const v = values(ctx);
  const gl = state.gl;
  const { feed, kill } = rates(v);

  const batch = Math.max(1, Math.round(v.stepsPerFrame * clamp01(ctx.quality)));
  gl.gl.disable(gl.gl.BLEND);

  let ping: GlTarget = state.ping;
  let pong: GlTarget = state.pong;
  const mask: GlTarget = state.mask ?? ping;

  for (let i = 0; i < batch && state.stepsDone < state.targetSteps; i++) {
    gl.bind(pong);
    gl.draw(state.simulateProgram, {
      uState: ping,
      uMask: mask,
      uTexel: [1 / state.simWidth, 1 / state.simHeight],
      uFeed: feed,
      uKill: kill,
      uDa: v.diffusionA,
      uDb: v.diffusionB,
      uAniso: v.anisotropy,
      uBoundary: v.textBoundary ? 1 : 0,
    });
    const swap = ping;
    ping = pong;
    pong = swap;
    state.stepsDone++;
  }

  state.ping = ping;
  state.pong = pong;

  state.dirty = true;
  return state;
}

export function isComplete(state: ReactionDiffusionState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: ReactionDiffusionState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);

  if (state.error) {
    // Say what happened rather than drawing nothing.
    const c = target.ctx;
    c.fillStyle = 'rgba(255,180,80,0.85)';
    c.font = `${Math.max(12, target.width / 48)}px ui-sans-serif, system-ui, sans-serif`;
    c.textAlign = 'center';
    c.fillText(state.error, target.width / 2, target.height / 2);
    return;
  }

  if (!state.gl || !state.ping || !state.displayProgram || !state.ramp) return;

  if (state.dirty) {
    const gl = state.gl;
    gl.bind(null, ctx.width, ctx.height);
    gl.gl.clearColor(0, 0, 0, 0);
    gl.gl.clear(gl.gl.COLOR_BUFFER_BIT);
    gl.gl.enable(gl.gl.BLEND);
    gl.gl.blendFunc(gl.gl.SRC_ALPHA, gl.gl.ONE_MINUS_SRC_ALPHA);
    gl.draw(state.displayProgram, {
      uState: state.ping,
      uRamp: state.ramp,
      uContrast: v.contrast,
      uOpacity: v.opacity,
    });

    state.octx.setTransform(1, 0, 0, 1, 0, 0);
    state.octx.clearRect(0, 0, ctx.width, ctx.height);
    state.octx.drawImage(state.gl.canvas as CanvasImageSource, 0, 0);
    state.dirty = false;
  }

  target.ctx.drawImage(state.output as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: ReactionDiffusionState): number {
  return state.simWidth * state.simHeight;
}
